import { GET, POST } from '@/shared/api/requests';
import type { Lead, CreateLeadInput, ExtractedLeadFields, StakeholderType } from '../types';
import { LEADS_BASE_URL } from '../constants';

/**
 * Fetch all stakeholder types (for step 1 and for display).
 */
export async function getStakeholderTypes(): Promise<StakeholderType[]> {
  const response = await GET<StakeholderType[] | { results: StakeholderType[] }>(
    `${LEADS_BASE_URL}/stakeholder-types/`
  );
  const data = response.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
    return data.results;
  }
  return [];
}

/**
 * Fetch all leads for the current user (or all if staff).
 */
export async function getLeads(): Promise<Lead[]> {
  const response = await GET<Lead[] | { results: Lead[] }>(`${LEADS_BASE_URL}/`);
  const data = response.data;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
    return data.results;
  }
  return [];
}

/**
 * Create a new lead.
 */
export async function createLead(input: CreateLeadInput): Promise<Lead> {
  const response = await POST<CreateLeadInput>(`${LEADS_BASE_URL}/`, {
    ...input,
    prospect_temperature: input.prospect_temperature ?? 'warm',
    status: input.status ?? 'new',
  });
  return response.data as Lead;
}

/**
 * Send an image (business card) to the backend for extraction.
 * Returns extracted contact fields to pre-fill the lead form.
 */
export async function extractFromImage(imageFile: File): Promise<ExtractedLeadFields> {
  const formData = new FormData();
  formData.append('image', imageFile);
  const response = await POST<FormData>(`${LEADS_BASE_URL}/extract_from_image/`, formData);
  return response.data as ExtractedLeadFields;
}

/**
 * Upload the raw business card image for a lead. Stored in GCS at
 * gs://GS_BUCKET_NAME/leads/images/<LEAD_ID>/<ORIGINAL_FILENAME>
 */
export async function uploadLeadCardImage(leadId: string, imageFile: File): Promise<{ card_image_path: string }> {
  const formData = new FormData();
  formData.append('image', imageFile);
  const response = await POST<FormData>(`${LEADS_BASE_URL}/${leadId}/card_image/`, formData);
  return response.data as { card_image_path: string };
}
